import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { isEmpty } from 'lodash-es';
import { ActivatedRoute, ActivatedRouteSnapshot, ActivationEnd, Router } from '@angular/router';
import { UserPermission } from 'src/app/modules/auth/enums/user-permission.enum';
import { OrganizationData } from 'src/app/modules/organizations/interfaces/OrganizationData';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../services/mdr-dashboards.service';
import { TimeframeData } from '../../../timeframe/interfaces/TimeframeData';
import { TimeframeService } from '../../../timeframe/services/timeframe.service';
import { SecurityaddonsService } from 'src/app/modules/securityaddons/services/securityaddons.service';
import { NotificationsService } from '../../../notifications/services/notifications.service';
import { IdentityService } from '../../../auth/services/identity.service';
import { CompanyDataModel } from '../../../companies/company-data-model.model';
import { CompanyDetailsInterface } from '../../../companies/interfaces/company-details.interface';

enum DashboardTabs {
    ORG_SUMMARY = '/summary',
    OVERVIEW = '/overview',
    MDR_SERVICE = '/mdr-service',
    SERVICE = '/service',
    CONFIGURATION = '/configuration',
    AGENT_ACTIONS = '/agent-actions',
}

@Component({
    selector: 'app-mdr-dashboards-browser',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './mdr-dashboards-browser.component.html',
    styleUrls: ['./mdr-dashboards-browser.component.scss'],
    standalone: false,
})
export class MdrDashboardsBrowserComponent implements OnDestroy, OnInit {
    public UserPermission = UserPermission;
    public url: SafeResourceUrl;
    public hasDashboards = true;
    public isLoading = true;
    public activeOrganization: OrganizationData | null;
    public companyLoaded = false;
    protected routerEventsSubscription: Subscription;
    protected orgSubscription: Subscription;
    private tabsForEnterpriseMSP = [
        DashboardTabs.ORG_SUMMARY,
        DashboardTabs.OVERVIEW,
        DashboardTabs.MDR_SERVICE,
        DashboardTabs.CONFIGURATION,
        DashboardTabs.AGENT_ACTIONS,
    ];
    private tabsForEnterpriseNonMSP = [
        DashboardTabs.OVERVIEW,
        DashboardTabs.MDR_SERVICE,
        DashboardTabs.CONFIGURATION,
        DashboardTabs.AGENT_ACTIONS,
    ];
    private tabsForFoundations = [DashboardTabs.SERVICE, DashboardTabs.CONFIGURATION];

    public constructor(
        public identityService: IdentityService,
        public organizationService: OrganizationsService,
        public timeframeService: TimeframeService,
        public mdrDashboardsService: MdrDashboardsService,
        public cd: ChangeDetectorRef,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly injector: Injector,
        private securityAddOnsService: SecurityaddonsService,
        public notificationsService: NotificationsService,
    ) {
        this.navigateToPreviouslySelectedDashboard();
    }

    public ngOnInit(): void {
        const dashboardSelectedOrganization = JSON.parse(<string>localStorage.getItem('dashboardSelectedOrganization'));

        this.identityService.companyLoaded$.subscribe((value: boolean) => {
            this.navigateThroughMainTabs(this.identityService.company!);
            this.companyLoaded = value;
            this.cd.detectChanges();
        });

        this.orgSubscription = this.organizationService.selectedOrganization$().subscribe((org) => {
            this.navigateThroughMainTabs(org || this.identityService.company);
        });

        /* if no organization is selected from the selector, enable 'All managed companies' option */
        if (isEmpty(dashboardSelectedOrganization) && this.identityService.isCompanySelectorVisible()) {
            this.organizationService.selectAllOrganizations();
        } else {
            this.organizationService.selectOrganization(dashboardSelectedOrganization as OrganizationData);
        }

        this.timeframeService.selectTimeframe(
            this.timeframeService.currentlySelectedTimeframe()
                ? this.timeframeService.currentlySelectedTimeframe()
                : {
                      id: 1,
                      name: '24h',
                      label: '24 hours',
                  },
        );

        this.storeSelectedDashboard();
    }

    public navigateThroughMainTabs(company: OrganizationData) {
        const storedDashboard = localStorage.getItem('selectedDashboard')?.split('/').pop();
        if (this.mdrDashboardsService.isPremiumEnterprise(company)) {
            if (company?.details?.configuration.ui_settings.is_msp) {
                this.router.navigate([
                    `/dashboard/browse/${
                        storedDashboard && this.tabsForEnterpriseMSP.includes(<DashboardTabs>('/' + storedDashboard))
                            ? storedDashboard
                            : this.tabsForEnterpriseMSP[0]
                    }`,
                ]);
            } else {
                this.router.navigate([
                    `/dashboard/browse/${
                        storedDashboard && this.tabsForEnterpriseNonMSP.includes(<DashboardTabs>('/' + storedDashboard))
                            ? storedDashboard
                            : this.tabsForEnterpriseNonMSP[0]
                    }`,
                ]);
            }
        } else {
            if (company?.details?.configuration.ui_settings.is_msp) {
                this.router.navigate([
                    `/dashboard/browse/${
                        storedDashboard && this.tabsForEnterpriseMSP.includes(<DashboardTabs>('/' + storedDashboard))
                            ? storedDashboard
                            : this.tabsForEnterpriseMSP[0]
                    }`,
                ]);
            } else {
                this.router.navigate([
                    `/dashboard/browse/${
                        storedDashboard && this.tabsForFoundations.includes(<DashboardTabs>('/' + storedDashboard))
                            ? storedDashboard
                            : this.tabsForFoundations[0]
                    }`,
                ]);
            }
        }
    }

    /**
     * Listen to any change inside the organization selector.
     * Also update 'dashboardSelectedOrganization' variable from localStorage.
     * @param newOrganization - the new selected organization from the selector
     */
    public onOrganizationChange(newOrganization: OrganizationData | null) {
        this.isLoading = false;
        this.activeOrganization = newOrganization;
        localStorage.setItem('dashboardSelectedOrganization', JSON.stringify(newOrganization));
        const companyDetails = newOrganization ? (newOrganization as CompanyDetailsInterface) : this.identityService.company;
        this.securityAddOnsService.breachWarrantyIntegration = CompanyDataModel.findBreachWarranty(companyDetails);
        this.cd.detectChanges();
    }

    /**
     * Listen to any change inside the timeframe selector.
     * @param newTimeframe - the new selected timeframe from the selector
     */
    public onTimeframeChange(newTimeframe: TimeframeData | null) {
        this.timeframeService.selectTimeframe(newTimeframe);
    }

    public ngOnDestroy() {
        if (this.routerEventsSubscription) {
            this.routerEventsSubscription.unsubscribe();
        }

        if (this.orgSubscription) {
            this.orgSubscription.unsubscribe();
        }
    }

    private navigateToPreviouslySelectedDashboard(): void {
        const selectedDashboard = localStorage.getItem('selectedDashboard');
        if (selectedDashboard) {
            this.router.navigateByUrl(selectedDashboard);
        }
    }

    private storeSelectedDashboard(): void {
        this.routerEventsSubscription = this.router.events
            .pipe(
                /* get only activation end events */
                filter((event) => {
                    return event instanceof ActivationEnd;
                }),
                map((event) => event as ActivationEnd),
                /* get snapshot */
                map((event: ActivationEnd) => {
                    return event.snapshot;
                }),
                /* only get leaf route nodes */
                filter((route: ActivatedRouteSnapshot) => {
                    return route.firstChild === null;
                }),
            )
            .subscribe(() => {
                localStorage.setItem('selectedDashboard', this.router.url);
            });
    }
}
